<script setup lang="ts">
import { computed } from 'vue'
import PlateChip from '@/components/commons/PlateChip.vue'
import EmptyStateIllustration from '@/components/commons/EmptyStateIllustration.vue'
import type { components } from '@/api/types'

type Item = components['schemas']['PursuitRankerItem']

const props = defineProps<{ items: Item[] }>()
const emit = defineEmits<{ select: [plate: string] }>()

const top3 = computed(() => props.items.slice(0, 3))

const RANK_STYLES = [
  { label: '#1', accent: 'var(--danger)' },
  { label: '#2', accent: 'var(--warn)' },
  { label: '#3', accent: 'var(--info)' },
] as const
</script>

<template>
  <div v-if="top3.length === 0" class="top-pursuers-empty">
    <EmptyStateIllustration
      type="no-pursuits"
      title="Преследователей не найдено"
      description="За выбранный период никто не появлялся у нескольких машин."
    />
  </div>
  <div v-else class="top-pursuers-grid">
    <button
      v-for="(item, i) in top3"
      :key="item.plate"
      type="button"
      class="pursuer-card hover-lift fade-in"
      data-test="pursuer-card"
      :style="{ '--rank-accent': RANK_STYLES[i].accent }"
      @click="emit('select', item.plate)"
    >
      <div class="card-head">
        <span class="pursuer-rank">{{ RANK_STYLES[i].label }}</span>
        <PlateChip :plate="item.plate" />
        <span class="pursuer-plate-text sr-only">{{ item.plate }}</span>
        <span v-if="item.is_pursuer" class="pursuer-flag">преследователь</span>
      </div>
      <div class="card-body">
        <span class="pursuer-score">
          <span class="score-value">{{ item.score.toFixed(1) }}</span>
          <span class="score-label">балл</span>
        </span>
        <span class="meta"><strong>{{ item.appearances }}</strong> эп.</span>
        <span class="meta"><strong>{{ item.unique_cars }}</strong> маш.</span>
        <span class="cam-pill cam-front">П · {{ item.front_count }}</span>
        <span class="cam-pill cam-rear">З · {{ item.rear_count }}</span>
      </div>
    </button>
  </div>
</template>

<style scoped>
.top-pursuers-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--sp-4);
  margin-bottom: var(--sp-4);
}
@media (max-width: 767px) {
  .top-pursuers-grid { grid-template-columns: 1fr; }
}
.pursuer-card {
  background: var(--surface);
  border: 1px solid var(--line);
  border-top: 2px solid var(--rank-accent);
  border-radius: var(--r-card);
  padding: var(--sp-3);
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 6px;
  text-align: left;
  box-shadow: var(--shadow-1);
}
.card-head {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  width: 100%;
}
.pursuer-rank {
  font-size: var(--fs-xs);
  font-weight: 700;
  color: var(--rank-accent);
  letter-spacing: 0.08em;
}
.card-body {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 6px var(--sp-2);
  font-size: var(--fs-sm);
  color: var(--muted);
}
.pursuer-score { display: inline-flex; align-items: baseline; gap: 4px; }
.score-value { font-size: var(--fs-lg); font-weight: 600; color: var(--ink); }
.score-label { font-size: var(--fs-xs); color: var(--muted); text-transform: uppercase; letter-spacing: 0.05em; }
.meta strong { color: var(--ink); font-weight: 600; }
.cam-pill {
  font-size: var(--fs-xs);
  padding: 1px 6px;
  border-radius: var(--r-chip);
  font-family: var(--font-mono, monospace);
  color: white;
}
.cam-front { background: var(--c2); }
.cam-rear  { background: var(--warn); }
.top-pursuers-empty { margin-bottom: var(--sp-4); }
.pursuer-plate-text { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; }
.pursuer-flag {
  margin-left: auto;
  font-size: var(--fs-xs); font-weight: 600; color: white;
  background: var(--danger); padding: 1px 7px; border-radius: var(--r-chip);
}
</style>
